import { ArrowLeft, Ban } from "lucide-react";

import { useCancelContract, useContract } from "@/api/contract-query";
import { ShareButton } from "@/components/dashboard/share-btn";
import { StatusTag } from "@/components/order/status-tag";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ContractInvoiceDialog from "@/components/vendor/contract-invoice";
import { formatDate } from "@/lib/utils";
import { createFileRoute, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/contract/$contractid/")({
	component: ContractCard
});

export default function ContractCard() {
	const { contractid } = Route.useParams();
	const cancelContract = useCancelContract();
	const router = useRouter();
	const { data: contract, isPending, isError, refetch } = useContract({ contractId: contractid });
	const totalAmount = contract?.contractItem.reduce((sum, item) => sum + item.totalCost, 0) ?? 0;

	return (
		<section className="space-y-4">
			<div className="flex justify-between gap-2 w-full">

				<Button
					variant="secondary"
					className="gap-2 bg-white border"
					onClick={() => router.history.back()}
				>

					<ArrowLeft className="size-4" />
					Back
				</Button>
				<Button disabled={contract?.status.toLowerCase() === "expired"}>Edit Contract</Button>
			</div>
			<Card className="w-[40vw] max-lg:w-full mx-auto p-6 px-1 pt-10 shadow-none itesm-center justify-center">
				{
					isPending
						? (
								<div className="flex flex-col items-center justify-center gap-4 min-h-[80vh]">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
									<p className="text-muted-foreground">Loading...</p>
								</div>
							)
						: isError
							? <div>Something went wrong</div>
							: (
									<CardContent className="space-y-2">
										<div className="flex flex-col items-center gap-2">

											<ContractInvoiceDialog contract={contract} />
											<div className="text-sm text-muted-foreground">
												{formatDate(contract.createdAt)}
											</div>
											<div className="text-xs">
												CONTRACT STATUS:
												{" "}
												{/* @ts-expect-error invalid status */}
												<StatusTag status={contract?.status} />
											</div>
										</div>
										<div className="border-b p-4">
											<div className="flex justify-between items-center">
												<div className="text-muted-foreground">
													Contract ID:
													{contract.contractCode}
												</div>
												<ShareButton contractCode={contract?.contractCode ?? ""} contractName={contract?.title ?? ""} />

											</div>
											<div className="text-xl font-medium mt-2">
												N
												{totalAmount.toLocaleString()}
											</div>
										</div>
										<div className="border-b p-4">
											<div className="text-muted-foreground">Contract name</div>
											<div className="font-medium mt-1">{contract.title}</div>
										</div>
										<div className="border-b p-4">
											<div className="text-muted-foreground">Item details</div>
											<div className="font-medium mt-1">
												{contract.contractItem.map(item => item.productId.name).join(", ")}
											</div>
										</div>
										<div className="border-b p-4">
											<div className="text-muted-foreground">Shipping out date</div>
											<div className="font-medium mt-1">{new Date(contract.shippingDate).toLocaleDateString()}</div>
										</div>
										<div className="border-b p-4">
											<div className="text-muted-foreground">Estimated delivery date</div>
											<div className="font-medium mt-1">{new Date(contract.estimatedDeliveryDate).toLocaleDateString()}</div>
										</div>
										<div className="border-b p-4">
											<div className="text-muted-foreground">Pickup location</div>
											{/* <div className="font-medium mt-1">{contract.contractItem[0]?.productId.pickupLocation || "N/A"}</div> */}
										</div>
										<div className="flex flex-col gap-2">
											<Button variant="link" className="self-start px-0">More actions</Button>

											<Button
												variant="ghost"
												className="gap-2 justify-start px-0"
												disabled={contract.status.toLowerCase() === "canceled" || contract.status.toLowerCase() === "expired" || cancelContract.isPending}
												onClick={() => {
													cancelContract.mutateAsync(contract.id).then(() => refetch());
												}}
											>
												<Ban className="size-9 bg-red-200/60 font-light text-destructive p-1 rounded-full rotate-90" />
												<div className="flex flex-col items-start">
													<span className="text-destructive text-sm">Cancel Contract</span>
													<span className="text-xs">Click here to terminate contract</span>
												</div>
											</Button>
										</div>
									</CardContent>
								)
				}
			</Card>
		</section>
	);
}
